import React, { useMemo, useCallback } from 'react';
import type { DataSet, ColumnId, DataValue } from '../../../types';
import { DatasetAccessor } from '../../utils/DatasetAccessor';
import logger from '../../utils/logger';
import * as styles from './DummyCodingSettings.css';

export type ColumnAction = 'not_included' | 'include_as_is' | 'dummy_code' | 'dummy_code_with_original';

export interface ColumnActionMap {
  [columnId: string]: ColumnAction;
}

export type ScaleType = 'nominal' | 'ordinal' | 'interval' | 'ratio';

export interface ColumnScaleMap {
  [columnId: string]: ScaleType;
}

interface DummyCodingSettingsProps {
  dataset: DataSet;
  selectedColumns: ColumnId[];
  onSelectedColumnsChange: (columns: ColumnId[]) => void;
  columnActions: ColumnActionMap;
  onColumnActionsChange: (actions: ColumnActionMap) => void;
  columnScales: ColumnScaleMap;
  onColumnScalesChange: (scales: ColumnScaleMap) => void;
}

interface ColumnInfo {
  id: ColumnId;
  name: string;
  type: string;
  scale: string;
  isCategorical: boolean;
  isBoolean: boolean;
  uniqueValueCount?: number;
}

export const DummyCodingSettings: React.FC<DummyCodingSettingsProps> = ({
  dataset,
  selectedColumns,
  onSelectedColumnsChange,
  columnActions,
  onColumnActionsChange,
  columnScales,
  onColumnScalesChange
}) => {

  // 列のスケールを取得（オーバーライドがあればそれを使用）
  const getColumnScale = useCallback((col: { id: ColumnId; scale?: { scale?: string } }): string => {
    if (columnScales[col.id]) {
      return columnScales[col.id];
    }
    return col.scale?.scale || 'unknown';
  }, [columnScales]);

  // 列情報を取得
  const columnInfos = useMemo<ColumnInfo[]>(() => {
    if (!dataset.schema?.columns) return [];

    const accessor = dataset.data ? new DatasetAccessor(dataset as DataSet & { data: Record<string, DataValue[]> }) : null;

    return dataset.schema.columns.map(col => {
      const scale = getColumnScale(col);
      const isCategorical = scale === 'nominal' || scale === 'ordinal';
      const isBoolean = col.type === 'boolean';

      let uniqueValueCount: number | undefined;
      if (isCategorical && accessor) {
        try {
          const columnData = accessor.getColumnDataById(col.id);
          const uniqueValues = new Set(columnData.filter(v => v !== null && v !== undefined));
          uniqueValueCount = uniqueValues.size;
        } catch (err) {
          logger.warn('[DummyCodingSettings] Failed to get unique value count for column:', col.id, err);
        }
      }

      return {
        id: col.id,
        name: col.name,
        type: col.type,
        scale,
        isCategorical,
        isBoolean,
        uniqueValueCount
      };
    });
  }, [dataset, getColumnScale]);

  // 列のアクションを取得（デフォルト値を返す）
  const getColumnAction = useCallback((col: ColumnInfo): ColumnAction => {
    if (columnActions[col.id]) {
      return columnActions[col.id];
    }
    // デフォルト: include_as_is
    return 'include_as_is';
  }, [columnActions]);

  // 列のスケール変更ハンドラー
  const handleScaleChange = (columnId: ColumnId, scale: ScaleType) => {
    onColumnScalesChange({
      ...columnScales,
      [columnId]: scale
    });

    // スケール変更により、カテゴリカル→数値に変更された場合
    // dummy_code系アクションがあれば自動的にinclude_as_isに変更
    const newIsCategorical = scale === 'nominal' || scale === 'ordinal';
    const action = columnActions[columnId];
    if (!newIsCategorical && (action === 'dummy_code' || action === 'dummy_code_with_original')) {
      onColumnActionsChange({
        ...columnActions,
        [columnId]: 'include_as_is'
      });
    }
  };

  // 列のアクション変更ハンドラー
  const handleActionChange = (columnId: ColumnId, action: ColumnAction) => {
    onColumnActionsChange({
      ...columnActions,
      [columnId]: action
    });

    // selectedColumnsを更新
    if (action === 'not_included') {
      onSelectedColumnsChange(selectedColumns.filter(id => id !== columnId));
    } else {
      if (!selectedColumns.includes(columnId)) {
        onSelectedColumnsChange([...selectedColumns, columnId]);
      }
    }
  };

  // カテゴリカル変数でダミー変数化される列
  const columnsToEncode = useMemo(() => {
    return columnInfos.filter(col => {
      const action = getColumnAction(col);
      return col.isCategorical && !col.isBoolean && (action === 'dummy_code' || action === 'dummy_code_with_original');
    });
  }, [columnInfos, getColumnAction]);

  // 数値列としてそのまま含まれる列（元の列を残す場合も含む）
  const columnsAsIs = useMemo(() => {
    return columnInfos.filter(col => {
      const action = getColumnAction(col);
      return action === 'include_as_is' || action === 'dummy_code_with_original';
    });
  }, [columnInfos, getColumnAction]);

  // アクションドロップダウンのオプションを生成
  const getActionOptions = (col: ColumnInfo): Array<{ value: ColumnAction; label: string; disabled?: boolean }> => {
    const options: Array<{ value: ColumnAction; label: string; disabled?: boolean }> = [
      { value: 'not_included', label: 'Not included' },
      { value: 'include_as_is', label: 'Include as-is' }
    ];

    // カテゴリカル変数（boolean以外）のみダミー変数化が可能
    if (col.isCategorical && !col.isBoolean) {
      options.push({ value: 'dummy_code', label: 'Dummy code' });
      options.push({ value: 'dummy_code_with_original', label: 'Dummy code (keep original)' });
    } else {
      options.push({ value: 'dummy_code', label: 'Dummy code', disabled: true });
      options.push({ value: 'dummy_code_with_original', label: 'Dummy code (keep original)', disabled: true });
    }

    return options;
  };

  // エンコーディングプレビュー情報を生成
  const getEncodingPreview = (): string[] => {
    if (columnsToEncode.length === 0) {
      return ['No categorical variables selected for dummy coding'];
    }

    return columnsToEncode.map(col => {
      const uniqueCount = col.uniqueValueCount ?? 0;
      const dummyCount = uniqueCount - 1;
      return `• ${col.name} (${uniqueCount} unique value${uniqueCount !== 1 ? 's' : ''}) → ${dummyCount} dummy variable${dummyCount !== 1 ? 's' : ''}`;
    });
  };

  // 出力データセット情報を生成
  const getOutputInfo = (): { columnCount: number; rowCount: number } => {
    const asIsCount = columnsAsIs.length;
    const dummyCount = columnsToEncode.reduce((sum, col) => sum + ((col.uniqueValueCount ?? 0) - 1), 0);

    return {
      columnCount: asIsCount + dummyCount,
      rowCount: dataset.rowCount ?? 0
    };
  };

  const outputInfo = getOutputInfo();
  const encodingPreview = getEncodingPreview();

  // バリデーション
  const hasError = columnsToEncode.some(col => (col.uniqueValueCount ?? 0) <= 1);
  const errorMessage = hasError
    ? 'Some categorical variables have only one unique value and cannot be encoded'
    : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Configure columns for the transformed dataset:</h3>

        <table className={styles.columnTable}>
          <thead>
            <tr>
              <th>Column</th>
              <th>Type</th>
              <th>Scale</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {columnInfos.map(col => {
              const action = getColumnAction(col);
              const options = getActionOptions(col);
              const scaleOptions: ScaleType[] = ['nominal', 'ordinal', 'interval', 'ratio'];

              return (
                <tr key={col.id}>
                  <td>{col.name}</td>
                  <td>{col.type}</td>
                  <td>
                    <select
                      className={styles.actionDropdown}
                      value={col.scale}
                      onChange={(e) => handleScaleChange(col.id, e.target.value as ScaleType)}
                    >
                      {scaleOptions.map(scale => (
                        <option key={scale} value={scale}>
                          {scale}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className={styles.actionDropdown}
                      value={action}
                      onChange={(e) => handleActionChange(col.id, e.target.value as ColumnAction)}
                    >
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Encoding Preview:</h3>

        {columnsToEncode.length > 0 ? (
          <div className={styles.previewBox}>
            <p className={styles.previewTitle}>Categorical variables to be encoded:</p>
            <ul className={styles.previewList}>
              {encodingPreview.map((line, idx) => (
                <li key={idx}>{line}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className={styles.noSelection}>No categorical variables selected for dummy coding</p>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Output Dataset:</h3>
        <ul className={styles.summaryList}>
          <li>{outputInfo.columnCount} columns ({columnsAsIs.length} as-is + {columnsToEncode.reduce((sum, col) => sum + ((col.uniqueValueCount ?? 0) - 1), 0)} dummy variables)</li>
          <li>{outputInfo.rowCount.toLocaleString()} rows</li>
        </ul>
      </div>
    </div>
  );
};
